import { computed } from 'vue';
import { useAuthStore } from '../stores/auth';

/**
 * Composable for checking user permissions based on their role
 * @returns {Object} Permission checks and role information
 */
export function usePermissions() {
  const authStore = useAuthStore();

  // Current user's role
  const userRole = computed(() => authStore.userRole);

  // Role checks
  const isMember = computed(() => userRole.value === 'member');
  const isManager = computed(() => userRole.value === 'manager');
  const isAdministrator = computed(() => userRole.value === 'administrator');

  // Permission checks - what can the user do?

  // View permissions (all roles can view)
  const canViewEvents = computed(() => !!userRole.value);
  const canViewAttendees = computed(() => !!userRole.value);
  const canViewCampaigns = computed(() => !!userRole.value);
  const canViewLocations = computed(() => !!userRole.value);
  const canViewReports = computed(() => !!userRole.value);

  // Attendee management (all roles - members manage event rosters)
  const canCreateAttendees = computed(() => !!userRole.value);
  const canEditAttendees = computed(() => !!userRole.value);
  const canCheckInAttendees = computed(() => !!userRole.value);

  // Event management (managers and administrators only)
  const canCreateEvents = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canEditEvents = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canDeleteEvents = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );

  // Campaign management (managers and administrators only)
  const canCreateCampaigns = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canEditCampaigns = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canDeleteCampaigns = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );

  // Location management (managers and administrators only)
  const canCreateLocations = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canEditLocations = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canDeleteLocations = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );

  // Configuration management (managers and administrators)
  const canManagePresenters = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );
  const canManageCalendars = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );

  // Organization management (administrators only)
  const canManageUsers = computed(() => 
    userRole.value === 'administrator'
  );
  const canInviteUsers = computed(() => 
    userRole.value === 'administrator'
  );
  const canUpdateOrganization = computed(() => 
    userRole.value === 'administrator'
  );

  // Event roster management (administrators only)
  const canUnlockRoster = computed(() => 
    userRole.value === 'administrator'
  );

  // Event planner access (managers and administrators only)
  const canAccessPlanner = computed(() => 
    ['manager', 'administrator'].includes(userRole.value)
  );

  // Helper function to check if user has at least a certain role level
  function hasRoleLevel(requiredRole) {
    const roleLevels = {
      'member': 1,
      'manager': 2,
      'administrator': 3
    };
    const userLevel = roleLevels[userRole.value] || 0;
    const requiredLevel = roleLevels[requiredRole] || 0;
    return userLevel >= requiredLevel;
  }

  return {
    // Role info
    userRole,
    isMember,
    isManager,
    isAdministrator,

    // View permissions
    canViewEvents,
    canViewAttendees,
    canViewCampaigns,
    canViewLocations,
    canViewReports,

    // Attendee permissions
    canCreateAttendees,
    canEditAttendees,
    canCheckInAttendees,

    // Event permissions
    canCreateEvents,
    canEditEvents,
    canDeleteEvents,

    // Campaign permissions
    canCreateCampaigns,
    canEditCampaigns,
    canDeleteCampaigns,

    // Location permissions
    canCreateLocations,
    canEditLocations,
    canDeleteLocations,

    // Configuration permissions
    canManagePresenters,
    canManageCalendars,

    // Organization permissions
    canManageUsers,
    canInviteUsers,
    canUpdateOrganization,

    // Event roster permissions
    canUnlockRoster,

    // Planner permissions
    canAccessPlanner,

    // Helper
    hasRoleLevel
  };
}
